/** @format */
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import {
    OpenUniversityCart,
    OpenUniversityCartUpdateRequest,
    OpenUniversityUserBasicDetails,
    OtmId,
    StudyVoucherPayment,
    StudyVoucherPaymentCreationRequest,
    StudyVoucherPaymentCreationRetryRequest,
    StudyVoucherPurchaseNotReadyForActivationResponse,
    StudyVoucherPurchaseReadyForActivationResponse,
    StudyVoucherPurchaseStatusResponse,
} from 'common-typescript/types';
import { map, Observable, Subject, switchMap, tap } from 'rxjs';
import { OpenUniversityCartCustomerService } from './open-university-cart-customer.service';

const CONFIG = {
    ENDPOINTS: {
        backend: '/ilmo/api/open-university/study-voucher',
        createPayment() {
            return `${this.backend}/create-payment`;
        },
        retryCreatingPayment() {
            return `${this.backend}/retry-creating-payment`;
        },
        purchaseStatus(transactionId: string) {
            return `${this.backend}/purchase-status/${transactionId}`;
        },
        activate(transactionId: string) {
            return `${this.backend}/activate/${transactionId}`;
        },
        byOpenUniversityCartId(cartId: OtmId) {
            return `${this.backend}/study-voucher-payments/${cartId}`;
        },
    },
};

@Injectable({ providedIn: 'root' })
export class StudyVoucherPaymentService {
    constructor(
        private readonly http: HttpClient,
        private readonly openUniversityCartService: OpenUniversityCartCustomerService,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {}

    /**
     * Creates a new study voucher payment for the referred {@link OpenUniversityCart}, that is expected to be the user's current cart.
     * The cart is expected to be in TENTATIVE or RESERVED state. The given {@link language} and {@link buyerInformation} will be saved to
     * the cart, an {@link OpenUniversityCart.orderNumber} will be generated, and the cart (including its items) will be transitioned to
     * the RESERVED state (unless already there). Redirects the user to complete the payment.
     */
    initializeStudyVoucherPaymentAndNavigateToOpinFi(
        openUniversityCartId: OtmId,
        language: string,
        buyerInformation: OpenUniversityCartUpdateRequest,
    ): Observable<void> {
        const body: StudyVoucherPaymentCreationRequest = { openUniversityCartId, language, buyerInformation };
        return this.refreshCartAndNavigateToOpinFi(this.http.post<string>(CONFIG.ENDPOINTS.createPayment(), body));
    }

    /**
     * Creates a new study voucher payment for the referred {@link OpenUniversityCart} after an earlier payment attempt for the same cart
     * failed. Behaves like {@link initializeStudyVoucherPaymentAndNavigateToOpinFi}, except that no buyer information is sent, as it has
     * already been saved to the cart by the earlier attempt.
     */
    retryCreatingStudyVoucherPaymentAndNavigateToOpinFi(openUniversityCartId: OtmId, language: string): Observable<void> {
        const body: StudyVoucherPaymentCreationRetryRequest = { openUniversityCartId, language };
        return this.refreshCartAndNavigateToOpinFi(this.http.post<string>(CONFIG.ENDPOINTS.retryCreatingPayment(), body));
    }

    private refreshCartAndNavigateToOpinFi(paymentCreation$: Observable<string>): Observable<void> {
        return paymentCreation$.pipe(
            switchMap((redirectUrl: string) => {
                // Refresh the current cart, and wait for the refresh to complete before navigating to the payment system.
                // The refresh prevents stale cart state in the UI in browsers that open the payment system in a new tab.
                // Waiting for the refresh to complete prevents a system error alert that results from a cancelled request.
                const redirectUrl$ = new Subject<string>();
                this.openUniversityCartService.refreshCurrentCart().add(() => {
                    redirectUrl$.next(redirectUrl);
                    redirectUrl$.complete();
                });
                return redirectUrl$.asObservable();
            }),
            tap(redirectUrl => (this.document.location.href = redirectUrl)),
            map<string, void>(() => undefined),
        );
    }

    /**
     * <p>
     * The returned {@link Observable} emits (once) the status of a study voucher transaction, that was earlier started with
     * {@link initializeStudyVoucherPaymentAndNavigateToOpinFi}. Takes a transaction id delivered by Opin.fi after the user has been
     * redirected back from the payment URL.
     * </p>
     *
     * <p>
     * Returns a {@link StudyVoucherPurchaseReadyForActivationResponse}, if the purchase is ready to be activated.
     * Otherwise, returns a {@link StudyVoucherPurchaseNotReadyForActivationResponse}.
     * </p>
     */
    getPurchaseStatus(transactionId: string): Observable<StudyVoucherPurchaseStatusResponse> {
        return this.http.get<StudyVoucherPurchaseStatusResponse>(CONFIG.ENDPOINTS.purchaseStatus(transactionId));
    }

    /**
     * Activates the purchased studies for the given transaction. Returns the activated {@link OpenUniversityCart}, whose items and product
     * package details are rendered in the enrolment confirmed view.
     */
    activate(transactionId: string, openUniversityUserBasicDetails: OpenUniversityUserBasicDetails): Observable<OpenUniversityCart> {
        return this.http.post<OpenUniversityCart>(CONFIG.ENDPOINTS.activate(transactionId), openUniversityUserBasicDetails);
    }
    getByCartId(openUniversityCartId: OtmId): Observable<StudyVoucherPayment[]> {
        return this.http.get<StudyVoucherPayment[]>(CONFIG.ENDPOINTS.byOpenUniversityCartId(openUniversityCartId));
    }
}
